"use client"

import { useState, useEffect } from "react"
import { AuthLayout } from "@/components/auth/auth-layout"
import { Button } from "@/components/ui/button"
import { useRouter } from "@/src/hooks/useRouter"
import { User, Building2, Clock } from "lucide-react"
import { useKyc } from "@/src/contexts/KycContext"
import { useAuth } from "@/src/contexts/AuthContext"

export default function VerifyPage() {
  const [selectedType, setSelectedType] = useState<"individual" | "company" | null>(null)
  const router = useRouter()
  const { kycStatus, loading } = useKyc()
  const { logout } = useAuth()

  const handleLogout = () => {
    logout()
    router.push("/auth/login")
  }

  const handleNext = () => {
    if (selectedType === "individual") {
      router.push("/auth/verify/individual")
    } else if (selectedType === "company") {
      router.push("/auth/verify/company")
    }
  }

  // Auto-redirect approved users directly to dashboard — no status page shown
  useEffect(() => {
    if (!loading && kycStatus?.verified) {
      router.push("/dashboard")
    }
  }, [loading, kycStatus])

  if (loading || kycStatus?.verified) {
    return (
      <AuthLayout>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center">
          <p className="text-gray-600">กำลังตรวจสอบสถานะ...</p>
        </div>
      </AuthLayout>
    )
  }

  // Rejected - needs resubmission (CHECK THIS FIRST!)
  if (kycStatus?.status === 'rejected') {
    return (
      <AuthLayout>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto">
          <div className="mb-6 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              การยืนยันตัวตนไม่ผ่าน
            </h1>
            <p className="text-gray-600">
              กรุณาตรวจสอบและแก้ไขข้อมูลตามเหตุผลด้านล่าง
            </p>
          </div>

          {kycStatus.rejectionReason && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
              <p className="text-sm font-medium text-red-800 mb-1">เหตุผลการปฏิเสธ:</p>
              <p className="text-sm text-red-700">{kycStatus.rejectionReason}</p>
            </div>
          )}

          {kycStatus.verifiedType && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                ประเภทการยืนยัน:{" "}
                <span className="font-semibold">
                  {kycStatus.verifiedType === "individual" ? "บุคคลธรรมดา" : "นิติบุคคล"}
                </span>
              </p>
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={() => router.push(`/auth/verify/${kycStatus.verifiedType}`)}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              แก้ไขและส่งใหม่
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full"
            >
              ออกจากระบบ
            </Button>
          </div>
        </div>
      </AuthLayout>
    )
  }

  // Submitted but pending approval
  if (kycStatus?.submitted && !kycStatus?.verified) {
    return (
      <AuthLayout>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center">
          <div className="mb-6">
            <Clock className="w-20 h-20 text-yellow-500 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            กำลังตรวจสอบข้อมูล
          </h1>
          <p className="text-gray-600 mb-4">
            ข้อมูลการยืนยันตัวตนของคุณอยู่ระหว่างการตรวจสอบ
          </p>
          <p className="text-gray-500 text-sm mb-6">
            ทางเจ้าหน้าที่จะดำเนินการตรวจสอบให้เร็วที่สุด กรุณารอผลการพิจารณา
          </p>
          {kycStatus.verifiedType && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                ประเภทการยืนยัน:{" "}
                <span className="font-semibold">
                  {kycStatus.verifiedType === "individual" ? "บุคคลธรรมดา" : "นิติบุคคล"}
                </span>
              </p>
            </div>
          )}
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full"
          >
            ออกจากระบบ
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#323130] mb-2">
            กรอกข้อมูลสำหรับคำสั่งซื้อ
          </h1>
          <p className="text-[#949494] text-sm">เลือกประเภทการจดทะเบียน</p>
        </div>

        <div className="space-y-4 mb-8">
          {/* Individual Option */}
          <button
            onClick={() => setSelectedType("individual")}
            className={`w-full p-6 rounded-xl border-2 transition-all duration-200 text-left ${
              selectedType === "individual"
                ? "border-brand-primary bg-[#e4ffe5]"
                : "border-[#e5e5e5] bg-white hover:border-[#d1d1d1]"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedType === "individual"
                      ? "border-brand-primary"
                      : "border-[#d1d1d1]"
                  }`}
                >
                  {selectedType === "individual" && (
                    <div className="w-3 h-3 rounded-full bg-brand-primary" />
                  )}
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-[#e5e5e5] p-3 rounded-lg">
                    <User className="w-8 h-8 text-[#323130]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#323130]">บุคคลธรรมดา</h3>
                </div>

                <ul className="space-y-2 text-[#323130]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#949494] mt-1">•</span>
                    <span>เลขบัตรประจำตัวประชาชน</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#949494] mt-1">•</span>
                    <span>ข้อมูลที่อยู่สำหรับจัดส่งใบกำกับภาษี</span>
                  </li>
                </ul>
              </div>
            </div>
          </button>

          {/* Company Option */}
          <button
            onClick={() => setSelectedType("company")}
            className={`w-full p-6 rounded-xl border-2 transition-all duration-200 text-left ${
              selectedType === "company"
                ? "border-brand-primary bg-[#e4ffe5]"
                : "border-[#e5e5e5] bg-white hover:border-[#d1d1d1]"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedType === "company"
                      ? "border-brand-primary"
                      : "border-[#d1d1d1]"
                  }`}
                >
                  {selectedType === "company" && (
                    <div className="w-3 h-3 rounded-full bg-brand-primary" />
                  )}
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-[#e5e5e5] p-3 rounded-lg">
                    <Building2 className="w-8 h-8 text-[#323130]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#323130]">นิติบุคคล</h3>
                </div>

                <ul className="space-y-2 text-[#323130]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#949494] mt-1">•</span>
                    <span>ระบุประเภทธุรกิจ</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#949494] mt-1">•</span>
                    <span>เลขประจำตัวผู้เสียภาษีอากร</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#949494] mt-1">•</span>
                    <span>ข้อมูลบริษัทและที่อยู่เพื่อออกใบกำกับภาษี</span>
                  </li>
                </ul>
              </div>
            </div>
          </button>
        </div>

        <Button
          onClick={handleNext}
          disabled={!selectedType}
          variant="default"
          className="w-full"
          >
          ตกลง
        </Button>
      </div>
    </AuthLayout>
  )
}
